// R11P GEOLOGICAL P0.5-P9C — anonymous Public GIS client.
//
// Public GIS shows ONLY explicitly publicly-disclosed spatial information. The
// geo-api enforces this server-side: an anonymous caller is authorized a layer
// only when classification === 'public' AND is_published === true (operation
// 'view'); internal publication, grants, roles and investor status never apply
// to an anonymous caller (gisAccessPolicy.service.js). This client therefore
// makes DELIBERATELY UNCREDENTIALED requests — no bearer token, no cookies — so
// the server applies the anonymous public-only policy regardless of whether a
// session happens to exist in the browser. It must never be used for the
// authenticated analyst/investor GIS (that is gisService / P9E).
//
// Two further defences live here, independent of the backend:
//   • a WHITELIST projection of layer metadata (no grant ids, audit, staff,
//     internal notes, or unknown fields reach the UI);
//   • a geometry-only feature projection (feature `properties` are dropped, so
//     no attribute can be surfaced even if a popup is added later).

import { geoApi } from '@/lib/geoApi';
import {
  isServerPublicRow,
  normalizeGeometryType,
  parsePublicLayerEnvelope,
} from '@/lib/publicGisContract';
import type { GeoJsonFeature, GeoJsonFeatureCollection } from '@/services/gisService';

export interface PublicGisLayer {
  id: string;
  title: string;
  description: string;
  classification: string;
  geometryType?: string;
  available: boolean;
}

export interface PublicGisResult<T> {
  data: T | null;
  error: string | null;
  status: number;
}

// Base URL without the client's auth. We do NOT go through geoApi.request()
// because that attaches the session bearer and sends cookies.
const BASE = geoApi.baseUrl;

async function anonGet<T>(path: string): Promise<PublicGisResult<T>> {
  try {
    const res = await fetch(`${BASE}${path}`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      // Explicitly anonymous: no cookies and no auth header are sent.
      credentials: 'omit',
    });
    const isJson = res.headers.get('content-type')?.includes('application/json');
    const body = isJson ? await res.json().catch(() => null) : null;
    if (!res.ok) {
      return { data: null, error: `Request failed (${res.status})`, status: res.status };
    }
    return { data: (body as T) ?? null, error: null, status: res.status };
  } catch {
    return { data: null, error: 'Public GIS could not be reached.', status: 0 };
  }
}

// WHITELIST: only these fields ever leave the service. Unknown/internal fields
// (grant ids, audit, staff, notes, workflow state, metadata) are discarded here.
// P9C-FIX: the server row is snake_case — `geometry_type` is normalised here so
// the rest of the Portal keeps its camelCase model — and a row is accepted only
// when the server labelled it public (no client-side default to 'public').
function toPublicLayer(raw: unknown): PublicGisLayer | null {
  if (!raw || typeof raw !== 'object') return null;
  if (!isServerPublicRow(raw)) return null;
  const r = raw as Record<string, unknown>;
  const id = r.id;
  if (typeof id !== 'string' && typeof id !== 'number') return null;
  const title = typeof r.name === 'string' && r.name.trim()
    ? r.name
    : (typeof r.title === 'string' ? r.title : 'Untitled layer');
  return {
    id: String(id),
    title: String(title),
    description: typeof r.description === 'string' ? r.description : '',
    classification: 'public',
    geometryType: normalizeGeometryType(r.geometry_type),
    available: r.is_published !== false,
  };
}

export const publicGisService = {
  /**
   * The explicit public layer catalogue. The server returns only public +
   * published layers for an anonymous caller; we additionally project to the
   * whitelist above. Returns the raw result status so the UI can distinguish
   * unavailable (transport/5xx) from a genuine empty (200 + []).
   */
  async listPublicLayers(): Promise<PublicGisResult<PublicGisLayer[]>> {
    const res = await anonGet<unknown>('/gis/layers');
    if (res.error || !res.data) return { data: null, error: res.error, status: res.status };
    // P9C-FIX: the contract is { layers, count }. A body that is not that
    // envelope is UNAVAILABLE — never silently an empty catalogue.
    const rows = parsePublicLayerEnvelope(res.data);
    if (rows === null) {
      return { data: null, error: 'Public GIS returned an unexpected response.', status: res.status };
    }
    const layers = rows.map(toPublicLayer).filter((l): l is PublicGisLayer => l !== null);
    return { data: layers, error: null, status: res.status };
  },

  /**
   * Geometry for one public layer, PROPERTIES STRIPPED. The map draws geometry
   * only; no feature attribute is ever handed to the UI.
   */
  getPublicLayerFeatures: async (layerId: string): Promise<{
    data: GeoJsonFeatureCollection | null;
    error: string | null;
  }> => {
    const res = await anonGet<{ features?: unknown[] }>(
      `/gis/layers/${encodeURIComponent(layerId)}/features`,
    );
    if (res.error || !res.data) return { data: null, error: res.error };
    const rawFeatures = Array.isArray(res.data.features) ? res.data.features : [];
    const features: GeoJsonFeature[] = rawFeatures
      .map((f) => {
        const geom = f && typeof f === 'object' ? (f as Record<string, unknown>).geometry : null;
        // Geometry only — properties are deliberately dropped (never surfaced).
        return geom
          ? { type: 'Feature' as const, geometry: geom as GeoJsonFeature['geometry'], properties: null }
          : null;
      })
      .filter((f): f is GeoJsonFeature => f !== null);
    return { data: { type: 'FeatureCollection', features }, error: null };
  },
};
